import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './staff-design-system.css';
import './Staff.css';
import './StaffDashboard.css';
import { BarChartMonth } from '../../components/staff/ui/BarChartMonth';
import { DuotoneIcon } from '../../components/staff/ui/DuotoneIcon';
import { StaffLoadError } from '../../components/staff/StaffLoadError';
import { apiFetch, TENANT_ID } from '../../utils/api';

interface Revenue {
  caTTC: number;
  caHT: number;
  tickets: number;
  couverts: number;
  ticketMoyenTTC: number;
}
interface SparkPoint { date: string; label: string; caTTC: number; }
interface Comparison { previousWeekSameDayTTC: number; deltaPct: number; trend: 'up' | 'down' | 'flat'; }
interface DashboardSummary {
  revenue: Revenue | null;
  tables: { number: number }[];
  weekSparkline: SparkPoint[];
  comparison: Comparison | null;
}
type DayCA = { caTTC: number; tickets: number; couverts: number };

const eur = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`;

/** Clé "YYYY-MM-DD" locale (la France n'a qu'un fuseau, getDate() local suffit). */
function keyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
/** Décale une date de n jours, ancré à midi pour rester insensible aux DST. */
function addDays(date: Date, n: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  d.setDate(d.getDate() + n);
  return d;
}
function pctDelta(cur: number, prev: number): number | null {
  if (!prev) return null;
  return ((cur - prev) / prev) * 100;
}

function DeltaLine({ value, suffix }: { value: number | null; suffix: string }) {
  if (value == null) {
    return <span className="kpi3-delta kpi3-delta-flat">— {suffix}</span>;
  }
  const trend = value > 0.5 ? 'up' : value < -0.5 ? 'down' : 'flat';
  const arrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
  return (
    <span className={`kpi3-delta kpi3-delta-${trend}`}>
      {arrow} {value >= 0 ? '+' : ''}{value.toFixed(0)}% {suffix}
    </span>
  );
}

export function StaffDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [monthSales, setMonthSales] = useState<Record<string, DayCA>>({});
  const [prevMonthSales, setPrevMonthSales] = useState<Record<string, DayCA>>({});
  const [salesReady, setSalesReady] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [authError, setAuthError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // CA live + tables ouvertes — polling 30s (comme l'ancien dashboard).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiFetch(`/pos/dashboard-summary`);
        // 401 = session expirée : la bannière globale gère l'action, on affiche
        // en plus un message inline (sinon le dashboard resterait vide à « — »).
        if (r.status === 401) { if (!cancelled) setAuthError(true); return; }
        if (!r.ok) throw new Error(`${r.status}`);
        const json = (await r.json()) as DashboardSummary;
        if (!cancelled) { setSummary(json); setAuthError(false); }
      } catch {
        /* garde la dernière valeur connue (erreur réseau transitoire) */
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Ventes journalières mois courant + mois précédent → fenêtre 30j glissants + KPI semaine/mois.
  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prev = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    const fetchMonth = (m: string) =>
      apiFetch(`/pos/sales-by-month?month=${m}`)
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({} as Record<string, DayCA>));
    Promise.all([fetchMonth(cur), fetchMonth(prev)]).then(([c, p]) => {
      if (cancelled) return;
      setMonthSales(c || {});
      setPrevMonthSales(p || {});
      setSalesReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  // Compteur messages non lus (Maïa → staff) pour le badge de la tuile Messages.
  useEffect(() => {
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const r = await apiFetch(`/staff-messages?status=unread&limit=1`);
        if (!r.ok) return;
        const json = (await r.json()) as { unread?: number };
        if (!cancelled) setUnreadMessages(typeof json.unread === 'number' ? json.unread : 0);
      } catch {
        /* silencieux : badge absent si l'appel échoue */
      }
    };
    loadUnread();
    const t = setInterval(loadUnread, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const openTables = summary?.tables?.length ?? 0;
  const hasSales = salesReady && (Object.keys(monthSales).length > 0 || Object.keys(prevMonthSales).length > 0);

  const metrics = useMemo(() => {
    const today = new Date();
    const todayKey = keyOf(today);

    // Série journalière unifiée (caTTC par date), mois précédent puis courant.
    const daily: Record<string, number> = {};
    for (const [k, v] of Object.entries(prevMonthSales)) daily[k] = v.caTTC;
    for (const [k, v] of Object.entries(monthSales)) daily[k] = v.caTTC;
    // CA du jour : valeur live du service (plus à jour que sales-by-month qui se fige au Z).
    const liveToday = summary?.revenue?.caTTC ?? 0;
    if (liveToday > 0) daily[todayKey] = liveToday;

    const at = (d: Date) => daily[keyOf(d)] ?? 0;
    const sumWindow = (end: Date, days: number) => {
      let s = 0;
      for (let i = 0; i < days; i++) s += at(addDays(end, -i));
      return s;
    };

    // JOUR
    const dayCA = daily[todayKey] ?? liveToday;
    const dayDelta = summary?.comparison?.deltaPct ?? pctDelta(dayCA, at(addDays(today, -7)));

    // SEMAINE — 7j roulants vs 7j précédents
    let weekCA = sumWindow(today, 7);
    if (weekCA === 0 && summary?.weekSparkline?.length) {
      weekCA = summary.weekSparkline.reduce((s, p) => s + p.caTTC, 0);
    }
    const weekDelta = pctDelta(weekCA, sumWindow(addDays(today, -7), 7));

    // MOIS en cours vs mois précédent à date équivalente
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1, 12);
    let monthCA = 0;
    for (let d = new Date(monthStart); keyOf(d) <= todayKey; d = addDays(d, 1)) monthCA += at(d);
    const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1, 12);
    let monthPrev = 0;
    for (let i = 0; i < today.getDate(); i++) monthPrev += at(addDays(prevMonthStart, i));
    const monthDelta = pctDelta(monthCA, monthPrev);

    // 30 jours glissants
    const points: SparkPoint[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = addDays(today, -i);
      points.push({ date: keyOf(d), label: String(d.getDate()), caTTC: at(d) });
    }
    const total30 = sumWindow(today, 30);
    const delta30 = pctDelta(total30, sumWindow(addDays(today, -30), 30));

    return { dayCA, dayDelta, weekCA, weekDelta, monthCA, monthDelta, points, total30, delta30 };
  }, [summary, monthSales, prevMonthSales]);

  const dayReady = summary !== null;

  const tiles: { label: string; icon: string; bg: string; to?: string; soon?: boolean; badge?: number }[] = [
    { label: 'Salle', icon: 'utensils', bg: '#E8927A', to: '/staff/salle', badge: openTables || undefined },
    { label: 'Réservations', icon: 'calendar-clock', bg: '#3A7D8E', to: '/staff/resa' },
    { label: 'Messages', icon: 'comment-dots', bg: '#C77DA8', to: '/staff/messages', badge: unreadMessages || undefined },
    { label: 'Encaisser', icon: 'cash-register', bg: '#5DAF8E', to: '/staff/payment' },
    { label: 'La Carte', icon: 'book-open-lines', bg: '#5C8E5C', to: '/staff/carte' },
    { label: 'Compta', icon: 'calculator', bg: '#7A6BAE', to: '/staff/compta' },
    { label: 'Scanner', icon: 'qrcode-read', bg: '#D97A3F', to: '/scan' },
    { label: 'Carte hebdo', icon: 'clipboard', bg: '#4A9FA8', to: '/staff/carte-hebdo' },
    { label: 'Agenda', icon: 'calendar-days', bg: '#7AB5D9', to: '/staff/agenda' },
    // POC caméra UniFi Protect (cf docs/camera-unifi-protect.md) : actif sur Nodo,
    // "à venir" (grisé, non cliquable) sur les autres tenants.
    TENANT_ID === 'nodo'
      ? { label: 'Caméra', icon: 'cctv', bg: '#3A6EA5', to: '/staff/camera' }
      : { label: 'Caméra', icon: 'cctv', bg: '#B8B8B8', soon: true },
    { label: 'Cadeaux', icon: 'gift-card', bg: '#D87B92', to: '/carte-cadeau' },
    { label: 'Fidélité', icon: 'ranking-star', bg: '#E4A8B4', to: '/compte' },
    { label: 'Avis clients', icon: 'stars', bg: '#B8B8B8', soon: true },
    { label: 'Communauté', icon: 'people-group', bg: '#B8B8B8', soon: true },
  ];

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/')}>‹</button>
        <h1>Dashboard</h1>
      </div>

      {authError && <StaffLoadError kind="auth" />}

      {/* Bloc 1 — 3 KPI principaux */}
      <div className="kpi3-row">
        <div className="kpi3-card">
          <span className="kpi3-label">Jour</span>
          <span className="kpi3-value">{dayReady ? eur(metrics.dayCA) : '—'}</span>
          <DeltaLine value={dayReady ? metrics.dayDelta : null} suffix="vs J-7" />
        </div>
        <div className="kpi3-card">
          <span className="kpi3-label">Semaine</span>
          <span className="kpi3-value">{hasSales ? eur(metrics.weekCA) : '—'}</span>
          <DeltaLine value={hasSales ? metrics.weekDelta : null} suffix="vs S-1" />
        </div>
        <div className="kpi3-card">
          <span className="kpi3-label">Mois</span>
          <span className="kpi3-value">{hasSales ? eur(metrics.monthCA) : '—'}</span>
          <DeltaLine value={hasSales ? metrics.monthDelta : null} suffix="vs M-1" />
        </div>
      </div>

      {/* Bloc 2 — Évolution 30 jours glissants */}
      <div className="dash30">
        <div className="dash30-head">
          <span className="dash30-title">Évolution 30 jours</span>
          <DeltaLine value={hasSales ? metrics.delta30 : null} suffix="vs 30j" />
        </div>
        <div className="dash30-total">{hasSales ? eur(metrics.total30) : '—'}</div>
        <div className="dash30-bars">
          <BarChartMonth points={metrics.points} height={56} color="currentColor" />
        </div>
      </div>

      {/* Bloc 3 — Grille de navigation, style "iPhone" : carré pastel compact + label dessous */}
      <div className="pastel-grid">
        {tiles.map((t) => (
          <button
            key={t.label}
            className={`pastel-tile${t.soon ? ' pastel-tile-soon' : ''}`}
            onClick={() => (t.soon ? showToast('Bientôt disponible') : t.to && navigate(t.to))}
          >
            <span className="pastel-icon" style={{ background: t.bg }}>
              <DuotoneIcon name={t.icon} size={36} className="pastel-fa" />
              {t.badge ? <span className="pastel-badge">{t.badge}</span> : null}
            </span>
            <span className="pastel-label">{t.label}</span>
          </button>
        ))}
      </div>

      {toast && <div className="dash-toast" role="status">{toast}</div>}
    </div>
  );
}
